<template>
  <Transition name="modal-backdrop" appear>
    <div
      v-show="showModal"
      class="full-modal fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm"
      @mousedown="$emit('update:modelValue', false)"
    >
      <Transition name="modal-content" appear>
        <div
          v-show="showContent"
          ref="container"
          class="modal-content max-w-160 relative w-full rounded-2xl border border-gray-200/50 bg-white/95 shadow-glow backdrop-blur-md dark:border-gray-700/50 dark:bg-gray-900/95"
          :class="contentClass"
          @mousedown.stop
        >
          <slot />
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from '#imports'
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'

interface Props {
  modelValue: boolean
  contentClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  contentClass: 'md:w-160',
})

defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const showModal = ref(false)
const showContent = ref(false)
const container = ref<HTMLElement>()

function checkValue (val: boolean) {
  if (val) {
    showModal.value = true
    nextTick(() => {
      showContent.value = true
      nextTick(() => {
        if (container.value) {
          disableBodyScroll(container.value)
        }
      })
    })
  } else {
    clearAllBodyScrollLocks()
    showContent.value = false
    nextTick(() => {
      showModal.value = false
    })
  }
}

watch(() => props.modelValue, (val) => {
  checkValue(val)
})

onMounted(() => {
  checkValue(props.modelValue)
})

onBeforeUnmount(() => {
  clearAllBodyScrollLocks()
})
</script>

<style lang="scss" scoped>
/* Backdrop Animation */
.modal-backdrop-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-backdrop-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-backdrop-enter-from,
.modal-backdrop-leave-to {
  opacity: 0;
  backdrop-filter: blur(0px);
}

/* Content Animation */
.modal-content-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-content-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-content-enter-from {
  opacity: 0;
  transform: scale(0.8) translateY(-20px);
  filter: blur(4px);
}

.modal-content-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(10px);
  filter: blur(2px);
}

/* Enhanced modal styling */
.modal-content {
  will-change: transform, opacity, filter;
  transform-origin: center center;
}

/* Smooth backdrop blur transition */
.full-modal {
  will-change: backdrop-filter, opacity;
}
</style>
